import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _PropTypes from 'prop-types';
import { startOfMonth, subDays, startOfYear, max, startOfDay, subMonths, subYears, format, addMonths, addYears, subWeeks, addDays, addWeeks, isWithinInterval, startOfWeek, endOfWeek, endOfMonth, isSameDay, subHours, startOfHour, endOfDay, addHours, isSameHour, subMinutes, startOfMinute, endOfHour, addMinutes, differenceInCalendarDays, isAfter, isSameMonth, isBefore, startOfQuarter, addQuarters, subQuarters, maxTime, minTime } from 'date-fns';
import { rgba } from 'polished';
import * as React from 'react';
import { createElement } from 'react';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { getSpacing } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { isBeforeNoon } from '../../utils/date.js';
import { Button } from '../Button/Button.js';
import { IconArrowDown } from '../Icons/IconArrowDown.js';
import { IconArrowLeft } from '../Icons/IconArrowLeft.js';
import { IconArrowRight } from '../Icons/IconArrowRight.js';
import { IconArrowUp } from '../Icons/IconArrowUp.js';
import { Resizer } from '../Resizer/Resizer.js';
import { Select } from '../Select/Select.js';
import { TabsContext } from '../TabsContext/TabsContext.js';
import { TabsList } from '../TabsList/TabsList.js';
import { TabsPanel } from '../TabsPanel/TabsPanel.js';
import { TabsTab } from '../TabsTab/TabsTab.js';
import { jsxs, jsx } from 'react/jsx-runtime';

var daysPerWeek = 7;
var StyledDatePicker = styled.div.withConfig({
  displayName: "DatePicker__StyledDatePicker",
  componentId: "hge8sj-0"
})({
  display: "flex"
});
var StyledCalendar = styled.div.withConfig({
  displayName: "DatePicker__StyledCalendar",
  componentId: "hge8sj-1"
})({
  alignSelf: "flex-start",
  padding: "".concat(getSpacing(4), " ").concat(getSpacing(6))
});
var StyledHeader = styled.div.withConfig({
  displayName: "DatePicker__StyledHeader",
  componentId: "hge8sj-2"
})({
  display: "flex",
  justifyContent: "space-between"
});
var StyledTimePicker = styled.div.withConfig({
  displayName: "DatePicker__StyledTimePicker",
  componentId: "hge8sj-3"
})(function (_ref) {
  var theme = _ref.theme;
  return {
    borderLeft: "solid 1px ".concat(theme.neutralColors.silver),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: getSpacing(6)
  };
});
var StyledWheelContainer = styled.div.withConfig({
  displayName: "DatePicker__StyledWheelContainer",
  componentId: "hge8sj-4"
})({
  display: "flex",
  justifyContent: "center",
  width: 224
});
var StyledWheel = styled.div.withConfig({
  displayName: "DatePicker__StyledWheel",
  componentId: "hge8sj-5"
})(function (_ref2) {
  var disabled = _ref2.disabled,
      theme = _ref2.theme;
  return _objectSpread(_objectSpread({}, function () {
    if (disabled) {
      return {
        color: theme.neutralColors.rhino
      };
    }
  }()), {}, {
    width: getSpacing(8)
  });
});
var StyledWheelSeparator = styled.div.withConfig({
  displayName: "DatePicker__StyledWheelSeparator",
  componentId: "hge8sj-6"
})({
  alignItems: "center",
  display: "flex"
});
var StyledWheelValue = styled.input.withConfig({
  displayName: "DatePicker__StyledWheelValue",
  componentId: "hge8sj-7"
})({
  display: "block",
  textAlign: "center",
  maxWidth: "80%",
  margin: "0 auto !important",
  border: 0
});
var StyledTimeZone = styled.div.withConfig({
  displayName: "DatePicker__StyledTimeZone",
  componentId: "hge8sj-8"
})({
  marginTop: getSpacing(6)
});
var StyledPresetsContainer = styled.div.withConfig({
  displayName: "DatePicker__StyledPresetsContainer",
  componentId: "hge8sj-9"
})(function (_ref3) {
  var theme = _ref3.theme;
  return {
    borderRight: "solid 1px ".concat(theme.neutralColors.silver),
    padding: "".concat(getSpacing(4), " 0")
  };
});
var StyledPresets = styled.div.withConfig({
  displayName: "DatePicker__StyledPresets",
  componentId: "hge8sj-10"
})(function (_ref4) {
  var maxHeight = _ref4.maxHeight;
  return {
    maxHeight: maxHeight,
    overflowY: "auto",
    padding: "0 ".concat(getSpacing(6))
  };
});
var DatePicker = /*#__PURE__*/React.forwardRef(function DatePicker2(props, datesRef) {
  var _useThemeContext = useThemeContext(),
      i18n = _useThemeContext.i18n;

  var disabledAll = props.disabled,
      _props$locale = props.locale,
      locale = _props$locale === void 0 ? i18n.DatePicker.locale : _props$locale,
      variant = props.variant;

  var _React$useState = React.useState(224),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      calendarHeight = _React$useState2[0],
      setCalendarHeight = _React$useState2[1];

  var _React$useState3 = React.useState(true),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      isDateMode = _React$useState4[0],
      setIsDateMode = _React$useState4[1];

  var _React$useState5 = React.useState(getDatum(props)),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      datum = _React$useState6[0],
      setDatum = _React$useState6[1];

  var _React$useState7 = React.useState(),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      focused = _React$useState8[0],
      setFocused = _React$useState8[1];

  var _React$useState9 = React.useState(getInitialRangeMode(variant)),
      _React$useState10 = _slicedToArray(_React$useState9, 2),
      rangeMode = _React$useState10[0],
      setRangeMode = _React$useState10[1];

  function handleSelect(date) {
    switch (variant.type) {
      case "range":
        switch (rangeMode) {
          case "week":
            return variant.onChange(getInclRange({
              date: date,
              locale: locale,
              unit: "week"
            }));

          case "month":
            return variant.onChange(getInclRange({
              date: date,
              locale: locale,
              unit: "month"
            }));

          default:
            var value = variant.value;
            var nextRangeValue = value;

            switch (value.length) {
              case 0:
              case 2:
                nextRangeValue = [date];
                break;

              case 1:
                if (isBefore(date, value[0])) {
                  nextRangeValue = [date];
                } else if (isSameDay(date, value[0])) {
                  nextRangeValue = [];
                } else {
                  nextRangeValue = [value[0], date];
                }

                break;
            }

            return variant.onChange(nextRangeValue);
        }

      case "date":
        var nextDateValue = !variant.value || !isSameDay(date, variant.value) ? date : void 0;
        return variant.onChange(nextDateValue);
    }
  }

  var datePickerElement = /*#__PURE__*/jsxs(StyledDatePicker, {
    children: [function () {
      if (variant.type === "range" && variant.presets) {
        return /*#__PURE__*/jsx(StyledPresetsContainer, {
          children: /*#__PURE__*/jsx(StyledPresets, {
            maxHeight: calendarHeight,
            children: getPresetOptions({
              i18n: i18n,
              locale: locale
            }).map(function (_ref5) {
              var _ref6 = _slicedToArray(_ref5, 2),
                  label = _ref6[0],
                  getNextValue = _ref6[1];

              return /*#__PURE__*/jsx("div", {
                children: /*#__PURE__*/jsx(Button, {
                  disabled: disabledAll,
                  label: label,
                  onClick: function onClick() {
                    var nextValue = getNextValue();
                    setDatum(startOfMonth(nextValue[1]));
                    setFocused(nextValue[1]);
                    variant.onChange(nextValue);
                  },
                  variant: "ghost",
                  children: label
                })
              }, label);
            })
          })
        });
      }
    }(), /*#__PURE__*/jsx(Resizer, {
      onResize: function onResize(_ref7) {
        var height = _ref7.height;
        return setCalendarHeight(height);
      },
      children: function children(_ref8) {
        var ref = _ref8.ref;
        return /*#__PURE__*/jsxs(StyledCalendar, {
          ref: ref,
          children: [/*#__PURE__*/jsxs(StyledHeader, {
            children: [/*#__PURE__*/jsx(Button, {
              disabled: disabledAll,
              hideLabel: true,
              label: "Previous",
              onClick: function onClick() {
                var nextDatum = isDateMode ? subMonths(datum, 1) : subYears(datum, 1);
                setDatum(nextDatum);
                setFocused(nextDatum);
              },
              startAdornment: IconArrowLeft,
              variant: "ghost"
            }), /*#__PURE__*/jsx(Button, {
              disabled: disabledAll,
              label: "Select month",
              onClick: function onClick() {
                return setIsDateMode(!isDateMode);
              },
              variant: "ghost",
              children: isDateMode ? format(datum, "MMMM yyyy", {
                locale: locale
              }) : format(datum, "yyyy", {
                locale: locale
              })
            }), /*#__PURE__*/jsx(Button, {
              disabled: disabledAll,
              hideLabel: true,
              label: "Next",
              onClick: function onClick() {
                var nextDatum = isDateMode ? addMonths(datum, 1) : addYears(datum, 1);
                setDatum(nextDatum);
                setFocused(nextDatum);
              },
              startAdornment: IconArrowRight,
              variant: "ghost"
            })]
          }), isDateMode ? /*#__PURE__*/jsxs("div", {
            children: [/*#__PURE__*/jsx(DaysOfWeek, _objectSpread(_objectSpread({}, props), {}, {
              datum: datum,
              locale: locale
            })), /*#__PURE__*/jsx(Dates, _objectSpread(_objectSpread({}, props), {}, {
              datum: datum,
              focused: focused,
              locale: locale,
              onClick: function onClick(date) {
                setFocused(date);
                handleSelect(date);
              },
              onFocus: function onFocus() {
                return setFocused(cloneDate(datum));
              },
              onKeyDown: function onKeyDown(event) {
                if (!focused) {
                  return;
                }

                var nextFocused;
                var isNewPage = false;

                switch (event.key) {
                  case KEYS.ARROW_DOWN:
                    event.preventDefault();
                    nextFocused = addWeeks(focused, 1);
                    break;

                  case KEYS.ARROW_LEFT:
                    event.preventDefault();
                    nextFocused = subDays(focused, 1);
                    break;

                  case KEYS.ARROW_RIGHT:
                    event.preventDefault();
                    nextFocused = addDays(focused, 1);
                    break;

                  case KEYS.ARROW_UP:
                    event.preventDefault();
                    nextFocused = subWeeks(focused, 1);
                    break;

                  case KEYS.PAGE_UP:
                    event.preventDefault();
                    nextFocused = startOfMonth(subMonths(focused, 1));
                    isNewPage = true;
                    break;

                  case KEYS.PAGE_DOWN:
                    event.preventDefault();
                    nextFocused = startOfMonth(addMonths(focused, 1));
                    isNewPage = true;
                    break;

                  case KEYS.HOME:
                    event.preventDefault();
                    nextFocused = startOfYear(subYears(focused, 1));
                    isNewPage = true;
                    break;

                  case KEYS.END:
                    event.preventDefault();
                    nextFocused = startOfYear(addYears(focused, 1));
                    isNewPage = true;
                    break;

                  case KEYS.ENTER:
                  case KEYS.SPACE:
                    event.preventDefault();
                    handleSelect(focused);
                    break;
                }

                if (!nextFocused) {
                  return;
                }

                if (isNewPage || !isWithinInterval(nextFocused, {
                  start: startOfWeek(datum, {
                    locale: locale
                  }),
                  end: endOfWeek(endOfMonth(datum), {
                    locale: locale
                  })
                })) {
                  setDatum(startOfMonth(nextFocused));
                }

                setFocused(nextFocused);
              },
              ref: datesRef,
              variant: variant
            }))]
          }) : /*#__PURE__*/jsx(Months, {
            datum: datum,
            locale: locale,
            onClick: function onClick(nextDatum) {
              setIsDateMode(true);
              setDatum(nextDatum);
              setFocused(nextDatum);
            }
          })]
        });
      }
    }), function () {
      if (variant.type === "date" && variant.time) {
        var _variant$time$clock = variant.time.clock,
            clock = _variant$time$clock === void 0 ? "12hour" : _variant$time$clock;
        var disabled = disabledAll || !variant.value;
        var value = variant.value || datum;
        var is12Hour = clock === "12hour";
        return /*#__PURE__*/jsxs(StyledTimePicker, {
          children: [/*#__PURE__*/jsxs(StyledWheelContainer, {
            children: [/*#__PURE__*/jsx(Wheel, {
              labelDecrease: i18n.DatePicker.timezoneHoursDecrease,
              labelIncrease: i18n.DatePicker.timezoneHoursIncrease,
              labelInput: i18n.DatePicker.timezoneHoursLabel,
              disabled: disabled,
              onDecrease: function onDecrease() {
                var nextValue = isSameDay(value, subHours(value, 1)) ? subHours(value, 1) : startOfHour(endOfDay(value));
                variant.onChange(nextValue);
              },
              onIncrease: function onIncrease() {
                var nextValue = isSameDay(value, addHours(value, 1)) ? addHours(value, 1) : startOfDay(value);
                variant.onChange(nextValue);
              },
              value: format(value, is12Hour ? "h" : "H", {
                locale: locale
              })
            }), /*#__PURE__*/jsx(StyledWheelSeparator, {
              children: ":"
            }), /*#__PURE__*/jsx(Wheel, {
              labelDecrease: i18n.DatePicker.timezoneMinutesDecrease,
              labelIncrease: i18n.DatePicker.timezoneMinutesIncrease,
              labelInput: i18n.DatePicker.timezoneMinutesLabel,
              disabled: disabled,
              onDecrease: function onDecrease() {
                var nextValue = isSameHour(value, subMinutes(value, 1)) ? subMinutes(value, 1) : startOfMinute(endOfHour(value));
                variant.onChange(nextValue);
              },
              onIncrease: function onIncrease() {
                var nextValue = isSameHour(value, addMinutes(value, 1)) ? addMinutes(value, 1) : startOfHour(value);
                variant.onChange(nextValue);
              },
              value: format(value, "mm", {
                locale: locale
              })
            }), function () {
              if (is12Hour) {
                var am = isBeforeNoon(value);
                return /*#__PURE__*/jsx(Wheel, {
                  labelDecrease: i18n.DatePicker.timezoneMeridianDecrease,
                  labelIncrease: i18n.DatePicker.timezoneMeridianIncrease,
                  labelInput: i18n.DatePicker.timezoneMeridianLabel,
                  disabled: disabled,
                  onDecrease: function onDecrease() {
                    return variant.onChange(am ? addHours(value, 12) : subHours(value, 12));
                  },
                  onIncrease: function onIncrease() {
                    return variant.onChange(am ? addHours(value, 12) : subHours(value, 12));
                  },
                  value: format(value, "aa", {
                    locale: locale
                  })
                });
              }
            }()]
          }), variant.time.timezone && /*#__PURE__*/jsx(StyledTimeZone, {
            children: /*#__PURE__*/jsx(Select, _objectSpread(_objectSpread({}, variant.time.timezone), {}, {
              disabled: disabled,
              searchable: true
            }))
          })]
        });
      }
    }()]
  }, rangeMode);

  return /*#__PURE__*/jsx("div", {
    children: function () {
      if (variant.type === "range") {
        var rangeModes = variant.modes || ["dynamic"];

        if (rangeModes.length > 1) {
          return /*#__PURE__*/jsxs(TabsContext, {
            value: rangeMode,
            children: [/*#__PURE__*/jsx(TabsList, {
              onChange: function onChange(nextRangeMode) {
                variant.onChange([]);
                setRangeMode(nextRangeMode);
                setFocused(datum);
              },
              children: rangeModes.map(function (rm) {
                return /*#__PURE__*/jsx(TabsTab, {
                  value: rm,
                  children: {
                    dynamic: "Dynamic",
                    month: "Month",
                    week: "Week"
                  }[rm]
                }, rm);
              })
            }), /*#__PURE__*/jsx(TabsPanel, {
              value: rangeMode,
              children: datePickerElement
            })]
          });
        }
      }

      return datePickerElement;
    }()
  });
});
DatePicker.propTypes = {
  "disabled": _PropTypes.bool,
  "locale": _PropTypes.shape({
    "code": _PropTypes.string,
    "formatDistance": _PropTypes.func,
    "formatLong": _PropTypes.shape({
      "date": _PropTypes.func.isRequired,
      "dateTime": _PropTypes.func.isRequired,
      "time": _PropTypes.func.isRequired
    }),
    "formatRelative": _PropTypes.func,
    "localize": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "match": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "options": _PropTypes.shape({
      "firstWeekContainsDate": _PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
      "weekStartsOn": _PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])
    })
  }),
  "variant": _PropTypes.oneOfType([_PropTypes.shape({
    onChange: _PropTypes.func.isRequired,
    time: _PropTypes.shape({
      clock: _PropTypes.oneOf(['12hour', '24hour']),
      timezone: _PropTypes.shape({
        error: _PropTypes.bool,
        onChange: _PropTypes.func,
        options: _PropTypes.arrayOf(_PropTypes.object),
        value: _PropTypes.oneOfType([_PropTypes.arrayOf(_PropTypes.string), _PropTypes.string])
      })
    }),
    type: _PropTypes.oneOf(['date']).isRequired,
    value: _PropTypes.instanceOf(Date)
  }), _PropTypes.shape({
    modes: _PropTypes.arrayOf(_PropTypes.oneOf(['dynamic', 'month', 'week'])),
    onChange: _PropTypes.func.isRequired,
    presets: _PropTypes.bool,
    type: _PropTypes.oneOf(['range']).isRequired,
    value: _PropTypes.array
  })])
};
DatePicker.displayName = "DatePicker";
var StyledDaysOfWeek = styled.div.withConfig({
  displayName: "DatePicker__StyledDaysOfWeek",
  componentId: "hge8sj-11"
})({
  display: "flex",
  justifyContent: "space-between"
});
var StyledDayOfWeek = styled.div.withConfig({
  displayName: "DatePicker__StyledDayOfWeek",
  componentId: "hge8sj-12"
})({
  flex: "1 1 0%",
  fontWeight: 600,
  lineHeight: getSpacing(8),
  textAlign: "center"
});

var DaysOfWeek = function DaysOfWeek(_ref9) {
  var datum = _ref9.datum,
      locale = _ref9.locale;
  var start = startOfWeek(datum, {
    locale: locale
  });
  return /*#__PURE__*/jsx(StyledDaysOfWeek, {
    children: range(daysPerWeek).map(function (i) {
      return /*#__PURE__*/jsx(StyledDayOfWeek, {
        children: format(addDays(start, i), "EEEEE", {
          locale: locale
        })
      }, i);
    })
  });
};

DaysOfWeek.propTypes = {
  "datum": _PropTypes.instanceOf(Date).isRequired,
  "locale": _PropTypes.shape({
    "code": _PropTypes.string,
    "formatDistance": _PropTypes.func,
    "formatLong": _PropTypes.shape({
      "date": _PropTypes.func.isRequired,
      "dateTime": _PropTypes.func.isRequired,
      "time": _PropTypes.func.isRequired
    }),
    "formatRelative": _PropTypes.func,
    "localize": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "match": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "options": _PropTypes.shape({
      "firstWeekContainsDate": _PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
      "weekStartsOn": _PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])
    })
  })
};
DaysOfWeek.displayName = "DaysOfWeek";
var StyledWeek = styled.div.withConfig({
  displayName: "DatePicker__StyledWeek",
  componentId: "hge8sj-13"
})({
  display: "flex",
  justifyContent: "space-between"
});
var StyledDay = styled.div.withConfig({
  displayName: "DatePicker__StyledDay",
  componentId: "hge8sj-14"
})(function (_ref10) {
  var inRange = _ref10.inRange,
      muted = _ref10.muted,
      theme = _ref10.theme;
  return _objectSpread(_objectSpread(_objectSpread({}, function () {
    if (muted) {
      return {
        opacity: 0.83
      };
    }
  }()), function () {
    if (inRange) {
      return {
        backgroundColor: rgba(theme.CTAColors.primary, 0.1)
      };
    }
  }()), {}, {
    flex: "1 1 0%",
    textAlign: "center"
  });
});
var StyledDates = styled.div.withConfig({
  displayName: "DatePicker__StyledDates",
  componentId: "hge8sj-15"
})({
  outline: "none"
});
var Dates = /*#__PURE__*/React.forwardRef(function Dates2(props, ref) {
  var datum = props.datum,
      locale = props.locale,
      onFocus = props.onFocus;
  var start = startOfWeek(datum, {
    locale: locale
  });

  var _React$useState11 = React.useState(),
      _React$useState12 = _slicedToArray(_React$useState11, 2),
      hovered = _React$useState12[0],
      setHovered = _React$useState12[1];

  var rowsPerMonth = Math.ceil(differenceInCalendarDays(endOfWeek(endOfMonth(datum), {
    locale: locale
  }), startOfWeek(datum, {
    locale: locale
  })) / daysPerWeek);
  return /*#__PURE__*/jsx(StyledDates, {
    onFocus: onFocus,
    onMouseLeave: function onMouseLeave() {
      return setHovered(void 0);
    },
    ref: ref,
    role: "grid",
    tabIndex: 0,
    children: range(rowsPerMonth).map(function (weekIndex) {
      return /*#__PURE__*/jsx(StyledWeek, {
        role: "row",
        children: range(daysPerWeek).map(function (dayIndex) {
          var date = addDays(addWeeks(start, weekIndex), dayIndex);
          return /*#__PURE__*/createElement(Day, _objectSpread(_objectSpread({}, props), {}, {
            date: date,
            hovered: hovered,
            key: dayIndex,
            onMouseEnter: function onMouseEnter() {
              return setHovered(date);
            }
          }));
        })
      }, weekIndex);
    })
  });
});
Dates.propTypes = {
  "datum": _PropTypes.instanceOf(Date).isRequired,
  "locale": _PropTypes.shape({
    "code": _PropTypes.string,
    "formatDistance": _PropTypes.func,
    "formatLong": _PropTypes.shape({
      "date": _PropTypes.func.isRequired,
      "dateTime": _PropTypes.func.isRequired,
      "time": _PropTypes.func.isRequired
    }),
    "formatRelative": _PropTypes.func,
    "localize": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "match": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "options": _PropTypes.shape({
      "firstWeekContainsDate": _PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
      "weekStartsOn": _PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])
    })
  }),
  "onFocus": _PropTypes.func
};
Dates.displayName = "Dates";

var Day = function Day(_ref11) {
  var date = _ref11.date,
      datum = _ref11.datum,
      disabledAll = _ref11.disabled,
      focused = _ref11.focused,
      hovered = _ref11.hovered,
      locale = _ref11.locale,
      _ref11$max = _ref11.max,
      max = _ref11$max === void 0 ? new Date(maxTime) : _ref11$max,
      _ref11$min = _ref11.min,
      min = _ref11$min === void 0 ? new Date(minTime) : _ref11$min,
      _onClick = _ref11.onClick,
      onKeyDown = _ref11.onKeyDown,
      onMouseEnter = _ref11.onMouseEnter,
      variant = _ref11.variant;
  var ref = React.useRef(null);
  var hasFocus = React.useMemo(function () {
    return !!focused && isSameDay(date, focused);
  }, [focused, date]);
  React.useEffect(function () {
    if (hasFocus) {
      var _ref$current;

      ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.focus();
    }
  }, [hasFocus]);
  var selected = false;
  var inRange = false;

  switch (variant.type) {
    case "date":
      selected = !!(variant.value && isSameDay(date, variant.value));
      break;

    case "range":
      selected = !!(variant.value[0] && isSameDay(date, variant.value[0])) || !!(variant.value[1] && isSameDay(date, variant.value[1]));

      switch (variant.value.length) {
        case 1:
          if (hovered && isAfter(hovered, variant.value[0])) {
            inRange = isWithinInterval(date, {
              start: variant.value[0],
              end: hovered
            });
          } else if (focused && isAfter(focused, variant.value[0])) {
            inRange = isWithinInterval(date, {
              start: variant.value[0],
              end: focused
            });
          }

          break;

        case 2:
          inRange = isWithinInterval(date, {
            start: variant.value[0],
            end: variant.value[1]
          });
          break;
      }

      break;
  }

  return /*#__PURE__*/jsx(StyledDay, {
    "aria-selected": selected,
    inRange: inRange,
    muted: !isSameMonth(date, datum),
    role: "gridcell",
    children: /*#__PURE__*/jsx(Button, {
      disabled: disabledAll || !isWithinInterval(date, {
        start: min,
        end: max
      }),
      label: "date",
      onClick: function onClick(event) {
        event.preventDefault();
        event.stopPropagation();

        _onClick(date);
      },
      onFocus: function onFocus(event) {
        return event.stopPropagation();
      },
      onKeyDown: onKeyDown,
      onMouseEnter: onMouseEnter,
      ref: ref,
      style: {
        paddingLeft: 0,
        paddingRight: 0,
        width: "100%"
      },
      variant: selected ? "primary" : "ghost",
      tabIndex: hasFocus ? 0 : -1,
      children: format(date, "d", {
        locale: locale
      })
    })
  });
};

Day.propTypes = {
  "date": _PropTypes.instanceOf(Date).isRequired,
  "datum": _PropTypes.instanceOf(Date).isRequired,
  "disabled": _PropTypes.bool,
  "focused": _PropTypes.instanceOf(Date),
  "hovered": _PropTypes.instanceOf(Date),
  "locale": _PropTypes.shape({
    "code": _PropTypes.string,
    "formatDistance": _PropTypes.func,
    "formatLong": _PropTypes.shape({
      "date": _PropTypes.func.isRequired,
      "dateTime": _PropTypes.func.isRequired,
      "time": _PropTypes.func.isRequired
    }),
    "formatRelative": _PropTypes.func,
    "localize": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "match": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "options": _PropTypes.shape({
      "firstWeekContainsDate": _PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
      "weekStartsOn": _PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])
    })
  }),
  "max": _PropTypes.instanceOf(Date),
  "min": _PropTypes.instanceOf(Date),
  "onClick": _PropTypes.func.isRequired,
  "onKeyDown": _PropTypes.func,
  "onMouseEnter": _PropTypes.func,
  "variant": _PropTypes.oneOfType([_PropTypes.shape({
    onChange: _PropTypes.func.isRequired,
    time: _PropTypes.shape({
      clock: _PropTypes.oneOf(['12hour', '24hour']),
      timezone: _PropTypes.shape({
        error: _PropTypes.bool,
        onChange: _PropTypes.func,
        options: _PropTypes.arrayOf(_PropTypes.object),
        value: _PropTypes.oneOfType([_PropTypes.arrayOf(_PropTypes.string), _PropTypes.string])
      })
    }),
    type: _PropTypes.oneOf(['date']).isRequired,
    value: _PropTypes.instanceOf(Date)
  }), _PropTypes.shape({
    modes: _PropTypes.arrayOf(_PropTypes.oneOf(['dynamic', 'month', 'week'])),
    onChange: _PropTypes.func.isRequired,
    presets: _PropTypes.bool,
    type: _PropTypes.oneOf(['range']).isRequired,
    value: _PropTypes.array
  })])
};
Day.displayName = "Day";

var Wheel = function Wheel(_ref12) {
  var labelIncrease = _ref12.labelIncrease,
      labelDecrease = _ref12.labelDecrease,
      labelInput = _ref12.labelInput,
      disabled = _ref12.disabled,
      onDecrease = _ref12.onDecrease,
      onIncrease = _ref12.onIncrease,
      value = _ref12.value;
  return /*#__PURE__*/jsxs(StyledWheel, {
    disabled: disabled,
    children: [/*#__PURE__*/jsx(Button, {
      disabled: disabled,
      hideLabel: true,
      label: labelIncrease,
      onClick: onIncrease,
      startAdornment: IconArrowUp,
      variant: "ghost"
    }), /*#__PURE__*/jsx(StyledWheelValue, {
      disabled: disabled,
      value: value,
      readOnly: true,
      "aria-label": labelInput
    }), /*#__PURE__*/jsx(Button, {
      disabled: disabled,
      hideLabel: true,
      label: labelDecrease,
      onClick: onDecrease,
      startAdornment: IconArrowDown,
      variant: "ghost"
    })]
  });
};

Wheel.propTypes = {
  "disabled": _PropTypes.bool,
  "labelDecrease": _PropTypes.string.isRequired,
  "labelIncrease": _PropTypes.string.isRequired,
  "labelInput": _PropTypes.string.isRequired,
  "onDecrease": _PropTypes.func.isRequired,
  "onIncrease": _PropTypes.func.isRequired,
  "value": _PropTypes.string.isRequired
};
Wheel.displayName = "Wheel";
var StyledMonthRow = styled.div.withConfig({
  displayName: "DatePicker__StyledMonthRow",
  componentId: "hge8sj-16"
})({
  display: "flex",
  justifyContent: "space-between",
  textAlign: "center",
  width: 224
});

var Months = function Months(_ref13) {
  var datum = _ref13.datum,
      locale = _ref13.locale,
      _onClick2 = _ref13.onClick;
  var start = startOfYear(datum);
  var monthsPerRow = 3;
  return /*#__PURE__*/jsx("div", {
    children: range(12 / monthsPerRow).map(function (i) {
      return /*#__PURE__*/jsx(StyledMonthRow, {
        children: range(monthsPerRow).map(function (j) {
          var offset = monthsPerRow * i + j;
          var nextDatum = addMonths(start, offset);
          return /*#__PURE__*/jsx(Button, {
            label: "Select month",
            onClick: function onClick() {
              return _onClick2(nextDatum);
            },
            variant: "ghost",
            children: format(nextDatum, "MMM", {
              locale: locale
            })
          }, offset);
        })
      }, i);
    })
  });
};

Months.propTypes = {
  "datum": _PropTypes.instanceOf(Date).isRequired,
  "locale": _PropTypes.shape({
    "code": _PropTypes.string,
    "formatDistance": _PropTypes.func,
    "formatLong": _PropTypes.shape({
      "date": _PropTypes.func.isRequired,
      "dateTime": _PropTypes.func.isRequired,
      "time": _PropTypes.func.isRequired
    }),
    "formatRelative": _PropTypes.func,
    "localize": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "match": _PropTypes.shape({
      "day": _PropTypes.func.isRequired,
      "dayPeriod": _PropTypes.func.isRequired,
      "era": _PropTypes.func.isRequired,
      "month": _PropTypes.func.isRequired,
      "ordinalNumber": _PropTypes.func.isRequired,
      "quarter": _PropTypes.func.isRequired
    }),
    "options": _PropTypes.shape({
      "firstWeekContainsDate": _PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7]),
      "weekStartsOn": _PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])
    })
  }),
  "onClick": _PropTypes.func.isRequired
};
Months.displayName = "Months";

function cloneDate(date) {
  return new Date(date.getTime());
}

function getDatum(_ref14) {
  var max = _ref14.max,
      min = _ref14.min,
      openTo = _ref14.openTo,
      variant = _ref14.variant;
  var date = openTo || getValue(variant);

  if (!date) {
    var current = new Date();

    if (min && isBefore(current, min)) {
      date = min;
    } else if (max && isAfter(current, max)) {
      date = max;
    } else {
      date = current;
    }
  }

  return startOfMonth(date);
}

function getInitialRangeMode(variant) {
  if (variant.type === "range") {
    return variant.modes && variant.modes.length !== 0 ? variant.modes[0] : "dynamic";
  }
}

function getInclRange(_ref15) {
  var _ref15$date = _ref15.date,
      date = _ref15$date === void 0 ? new Date() : _ref15$date,
      locale = _ref15.locale,
      unit = _ref15.unit;

  var _day$month$quarter$we = _slicedToArray({
    day: [startOfDay, addDays],
    month: [startOfMonth, addMonths],
    quarter: [startOfQuarter, addQuarters],
    week: [startOfWeek, addWeeks],
    year: [startOfYear, addYears]
  }[unit], 2),
      startOfUnit = _day$month$quarter$we[0],
      addUnits = _day$month$quarter$we[1];

  var start = startOfUnit(date, {
    locale: locale
  });
  return [start, subDays(addUnits(start, 1), 1)];
}

function getNextRange(_ref16) {
  var amount = _ref16.amount,
      _ref16$date = _ref16.date,
      date = _ref16$date === void 0 ? new Date() : _ref16$date,
      locale = _ref16.locale,
      unit = _ref16.unit;

  var _day$unit = _slicedToArray({
    day: [startOfDay, addDays]
  }[unit], 2),
      startOfUnit = _day$unit[0],
      addUnits = _day$unit[1];

  var start = startOfUnit(date, {
    locale: locale
  });
  return [start, subDays(addUnits(start, amount), 1)];
}

function getPrevRange(_ref17) {
  var amount = _ref17.amount,
      _ref17$date = _ref17.date,
      date = _ref17$date === void 0 ? new Date() : _ref17$date,
      locale = _ref17.locale,
      unit = _ref17.unit;

  var _day$month$quarter$we2 = _slicedToArray({
    day: [startOfDay, addDays, subDays],
    month: [startOfMonth, addMonths, subMonths],
    quarter: [startOfQuarter, addQuarters, subQuarters],
    week: [startOfWeek, addWeeks, subWeeks],
    year: [startOfYear, addYears, subYears]
  }[unit], 3),
      startOfUnit = _day$month$quarter$we2[0],
      addUnits = _day$month$quarter$we2[1],
      subUnits = _day$month$quarter$we2[2];

  var start = subUnits(startOfUnit(date, {
    locale: locale
  }), amount);
  return [start, subDays(addUnits(start, amount), 1)];
}

function getPresetOptions(_ref18) {
  var _ref18$date = _ref18.date,
      date = _ref18$date === void 0 ? new Date() : _ref18$date,
      i18n = _ref18.i18n,
      locale = _ref18.locale;
  var datePickeri18n = i18n.DatePicker;
  return [[datePickeri18n.today, function () {
    return getInclRange({
      date: date,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.yesterday, function () {
    return getPrevRange({
      date: date,
      amount: 1,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.last7Days, function () {
    return getPrevRange({
      date: date,
      amount: 7,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.last14Days, function () {
    return getPrevRange({
      date: date,
      amount: 14,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.last30Days, function () {
    return getPrevRange({
      date: date,
      amount: 30,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.last60Days, function () {
    return getPrevRange({
      date: date,
      amount: 60,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.last90Days, function () {
    return getPrevRange({
      date: date,
      amount: 90,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.next7Days, function () {
    return getNextRange({
      date: date,
      amount: 7,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.next14Days, function () {
    return getNextRange({
      date: date,
      amount: 14,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.next30Days, function () {
    return getNextRange({
      date: date,
      amount: 30,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.next60Days, function () {
    return getNextRange({
      date: date,
      amount: 60,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.next90Days, function () {
    return getNextRange({
      date: date,
      amount: 90,
      locale: locale,
      unit: "day"
    });
  }], [datePickeri18n.thisWeek, function () {
    return getInclRange({
      date: date,
      locale: locale,
      unit: "week"
    });
  }], [datePickeri18n.previousWeek, function () {
    return getPrevRange({
      date: date,
      amount: 1,
      locale: locale,
      unit: "week"
    });
  }], [datePickeri18n.previous2Weeks, function () {
    return getPrevRange({
      date: date,
      amount: 2,
      locale: locale,
      unit: "week"
    });
  }], [datePickeri18n.previous3Weeks, function () {
    return getPrevRange({
      date: date,
      amount: 3,
      locale: locale,
      unit: "week"
    });
  }], [datePickeri18n.previous4Weeks, function () {
    return getPrevRange({
      date: date,
      amount: 4,
      locale: locale,
      unit: "week"
    });
  }], [datePickeri18n.thisMonth, function () {
    return getInclRange({
      date: date,
      locale: locale,
      unit: "month"
    });
  }], [datePickeri18n.previousMonth, function () {
    return getPrevRange({
      date: date,
      amount: 1,
      locale: locale,
      unit: "month"
    });
  }], [datePickeri18n.previous2Months, function () {
    return getPrevRange({
      date: date,
      amount: 2,
      locale: locale,
      unit: "month"
    });
  }], [datePickeri18n.previous3Months, function () {
    return getPrevRange({
      date: date,
      amount: 3,
      locale: locale,
      unit: "month"
    });
  }], [datePickeri18n.previous6Months, function () {
    return getPrevRange({
      date: date,
      amount: 6,
      locale: locale,
      unit: "month"
    });
  }], [datePickeri18n.previous12Months, function () {
    return getPrevRange({
      date: date,
      amount: 12,
      locale: locale,
      unit: "month"
    });
  }], [datePickeri18n.thisQuarter, function () {
    return getInclRange({
      date: date,
      locale: locale,
      unit: "quarter"
    });
  }], [datePickeri18n.previousQuarter, function () {
    return getPrevRange({
      date: date,
      amount: 1,
      locale: locale,
      unit: "quarter"
    });
  }], [datePickeri18n.previous2Quarters, function () {
    return getPrevRange({
      date: date,
      amount: 2,
      locale: locale,
      unit: "quarter"
    });
  }], [datePickeri18n.previous3Quarters, function () {
    return getPrevRange({
      date: date,
      amount: 3,
      locale: locale,
      unit: "quarter"
    });
  }], [datePickeri18n.previous4Quarters, function () {
    return getPrevRange({
      date: date,
      amount: 4,
      locale: locale,
      unit: "quarter"
    });
  }], [datePickeri18n.thisYear, function () {
    return getInclRange({
      date: date,
      locale: locale,
      unit: "year"
    });
  }], [datePickeri18n.yearToDate, function () {
    var start = startOfYear(date);
    return [start, max([start, subDays(startOfDay(date), 1)])];
  }], [datePickeri18n.previousYear, function () {
    return getPrevRange({
      date: date,
      amount: 1,
      locale: locale,
      unit: "year"
    });
  }]];
}

function getValue(variant) {
  return variant.type === "date" ? variant.value : variant.value[0];
}

function range(arrayLength) {
  var startAt = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return Array.from(Array(arrayLength).keys()).map(function (i) {
    return i + startAt;
  });
}

export { DatePicker };
